/* eslint-disable @typescript-eslint/no-explicit-any */
import { CanvasEngineEntityController } from './entityController'
import {
  GRAB_BOX_COLOR,
  GRAB_BOX_BORDER_WIDTH,
  GRAB_BOX_FILL_OPACITY,
  GRAB_BOX_MIN_DISTANCE,
  GRAB_HIGHLIGHT_COLOR,
  GRAB_HIGHLIGHT_FILL_OPACITY,
  GRAB_HIGHLIGHT_BORDER_WIDTH,
  SHAPES,
  CANVAS_HANDLE_EVENT,
} from './const'
import type { CanvasEngineConstructor, Vector2d } from './types'
import { CanvasEngineEntity } from './entity'
import type {
  IGroup,
  ILayer,
  ILine,
  INode,
  IPointerEvent,
  IRect,
  IStage,
} from './backend/IRenderer'
import type { App as VueApp } from 'vue'
import { cloneDeep } from 'lodash'

// 액션버튼그룹 위치 관련 상수
const GRAB_BUTTON_POSITION_CONFIG = {
  GRABBOX_OFFSET_BELOW: 40, // grabbedBox 아래쪽 경계에서의 거리
  SCREEN_MARGIN: 10, // 화면 경계에서의 여백
  DEFAULT_ELEMENT_WIDTH: 100, // 엘리먼트 기본 너비
  DEFAULT_ELEMENT_HEIGHT: 80, // 엘리먼트 기본 높이
} as const

export class CanvasEngineGrabController extends CanvasEngineEntityController {
  // 그랩 박스 상태 관리
  private grabBox: {
    isDrawing: boolean
    startX: number
    startY: number
    rect?: IRect
    rectBorder?: IRect
    animationId?: number
    isCtrlPressed: boolean // 그랩박스 시작 시 ctrl 상태 기록
  } = {
    isDrawing: false,
    startX: 0,
    startY: 0,
    isCtrlPressed: false,
  }

  // 그랩 모드
  private isGrabMode: boolean = false

  // 그랩된 도형 id들
  private grabbedShapeIds: string[] = []

  // grab 중인 엔티티는 removeTargetCache로 비워 둔 상태를 유지 (제스처 후 보충 캐싱에서 제외)
  protected override isCacheExcluded(entity: CanvasEngineEntity): boolean {
    return (
      super.isCacheExcluded(entity) ||
      (this.isGrabMode && this.grabbedShapeIds.includes(entity.getId()))
    )
  }

  // 하이라이트들
  private highlights: Map<string, { rect: IRect; rectBorder: IRect }[]> = new Map()
  private previewHighlights: Map<string, { rect: IRect; rectBorder: IRect }[]> = new Map()

  // 그룹 경계 박스 (그랩된 모든 도형을 감싸는 사각형)
  private grabbedBox: {
    rect: IRect | null
    rectBorder: IRect | null
  } = {
    rect: null,
    rectBorder: null,
  }

  // ctrl/cmd 키 상태 추적
  private isCtrlPressed: boolean = false

  // 스페이스바 키 상태 추적 (자식 클래스에서 설정)
  protected isSpacePressed: boolean = false

  // 마지막 도형 그랩 해제 시 기존 선택 방지 플래그
  private preventNextGrab: boolean = false

  // ActionButtonGroup 관리
  private grabBoxButtonContainer: HTMLElement | null = null
  private grabBoxButtonElement: HTMLElement | null = null
  private grabBoxButtonApp: VueApp | null = null
  private grabBoxButtonAnimationFrameId: number | undefined
  private grabBoxButtonResizeObserver: ResizeObserver | undefined

  // 위치 캐싱
  private lastStagePosition: { x: number; y: number } = { x: 0, y: 0 }
  private lastStageScale: number = 1
  private lastGrabbedBoxBounds: { x: number; y: number; width: number; height: number } | null =
    null

  constructor(arg: CanvasEngineConstructor) {
    super(arg)
    // 생성자에서 ctrl/cmd 키 리스너 등록
    this.addCtrlKeyListener()
  }

  // 그랩 모드 종료
  public exitGrabMode(): void {
    this.isGrabMode = false

    const beforeGrabbedShapeIds = cloneDeep(this.grabbedShapeIds)
    this.grabbedShapeIds = []

    // ActionButtonGroup 제거
    this.clearActionButton()

    // 모든 하이라이트 제거
    this.clearHighlights()
    this.clearHighlights(true)

    // 그룹 경계 박스 제거
    this.clearGrabbedBox()

    // 상태에 맞게 도형 리스닝 설정
    this.setEntityListeningForGrab()

    // ActionButtonGroup 한번 더 제거
    this.clearActionButton()

    //그랩되었던 항목들의 캐시를 다시 수행
    if (beforeGrabbedShapeIds.length > 0) {
      const entities = beforeGrabbedShapeIds.map(id => this.getEntityById(id))
      if (entities.every(entity => entity !== undefined) && entities.length > 0) {
        this.restoreTargetCache(entities)
      }
    }
  }

  // 모바일 다중선택 모드 활성화
  public enableMobileSelectMode(): void {
    this.isMobileSelectMode = true
    this.addGrabBoxEvents()
  }

  // 모바일 다중선택 모드 비활성화
  public disableMobileSelectMode(): void {
    this.isMobileSelectMode = false
    this.exitGrabMode()
  }

  private isDraggingGrabBox = false
  // 그랩박스 이벤트 추가
  addGrabBoxEvents(): void {
    const stage = this.renderer.stage
    if (!stage) return

    // 앱환경에서는 grab 이벤트 비활성화 (단, 모바일 다중선택 모드 제외)
    if (this.isMobile && !this.isMobileSelectMode) return

    // 기존 이벤트 제거
    const defaultMouseEvents = `${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_MOUSE_UP}`
    const defaultTouchEvents = `${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_TOUCH_START} ${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_TOUCH_MOVE} ${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_TOUCH_END}`
    this.eventManager.removeEventFromString(stage, defaultMouseEvents)
    this.eventManager.removeEventFromString(stage, defaultTouchEvents)

    // 마우스/터치 다운 이벤트
    this.eventManager.addEventFromString(
      stage,
      `${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_MOUSE_DOWN} ${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_TOUCH_START}`,
      (e: IPointerEvent) => {
        // 모바일 다중선택 모드가 비활성화된 경우 무시
        if (this.isMobile && !this.isMobileSelectMode) return
        // 스페이스바 패닝 중이면 무시
        if (this.isSpacePressed) return

        const isTouchEvent = typeof TouchEvent !== 'undefined' && e.evt instanceof TouchEvent
        const isLeftClick = !isTouchEvent && (e.evt as MouseEvent).button === 0

        // ctrl+클릭: grab 모드에서는 entity listening=false이므로 stage에서 처리
        // 단일 선택에서는 entity click 핸들러가 처리
        if (
          !isTouchEvent &&
          ((e.evt as MouseEvent).ctrlKey || (e.evt as MouseEvent).metaKey) &&
          isLeftClick
        ) {
          if (this.isGrabMode) {
            const clickedEntity = this.findEntityFromClick(e)
            if (clickedEntity) this.ctrlSelectEntity(clickedEntity)
          }
          return
        }
        // 그랩박스 시작 (좌클릭 또는 단일 터치)
        const isSingleTouch = isTouchEvent && (e.evt as TouchEvent).touches.length === 1
        if (e.target === stage && (isLeftClick || isSingleTouch)) {
          this.isDraggingGrabBox = true
          this.handleGrabBoxDragStart()
        }
      },
    )

    this.eventManager.addEventFromString(
      stage,
      `${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_MOUSE_MOVE} ${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_TOUCH_MOVE}`,
      (e: IPointerEvent) => {
        if (!this.isDraggingGrabBox) return
        const isTouchEvent = typeof TouchEvent !== 'undefined' && e.evt instanceof TouchEvent
        // 핀치 중(멀티터치)이면 그랩박스 취소
        if (isTouchEvent && (e.evt as TouchEvent).touches.length > 1) {
          this.isDraggingGrabBox = false
          this.clearGrabBox()
          return
        }
        if (e.target === stage) this.handleGrabBoxDrag()
      },
    )

    this.eventManager.addEventFromString(
      stage,
      `${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_MOUSE_UP} ${CANVAS_HANDLE_EVENT.SYSTEM_GRAB_TOUCH_END}`,
      (e: IPointerEvent) => {
        if (!this.isDraggingGrabBox) return
        const isTouchEvent = typeof TouchEvent !== 'undefined' && e.evt instanceof TouchEvent
        const isLeftClick = !isTouchEvent && (e.evt as MouseEvent).button === 0
        if (e.target === stage && (isLeftClick || isTouchEvent)) {
          this.handleGrabEnd()
          this.isDraggingGrabBox = false
        }
      },
    )
  }

  // ctrl/cmd 키 이벤트 리스너 추가
  private addCtrlKeyListener(): void {
    // 앱환경에서는 ctrl/cmd 키 리스너 비활성화
    if (this.isMobile) return
    if (typeof window === 'undefined') return

    // keydown/keyup
    window.addEventListener('keydown', e => {
      if (e.key === 'Control' || e.key === 'Meta') this.isCtrlPressed = true
    })
    window.addEventListener('keyup', e => {
      if (e.key === 'Control' || e.key === 'Meta') this.isCtrlPressed = false
    })

    window.addEventListener('blur', () => {
      this.isCtrlPressed = false
    })
  }

  // 그랩 박스 범위 내 도형 찾기
  private findOnGrabBoxEntities(
    grabX1: number,
    grabY1: number,
    grabX2: number,
    grabY2: number,
  ): string[] {
    const entities = this.getEntities()
    const hitEntityIds = new Set<string>()

    // grabBox를 Vector2d[]로 변환 (왼쪽위부터 시계방향)
    const grabHitBox: Vector2d[] = [
      { x: Math.min(grabX1, grabX2), y: Math.min(grabY1, grabY2) }, // 왼쪽위
      { x: Math.max(grabX1, grabX2), y: Math.min(grabY1, grabY2) }, // 오른쪽위
      { x: Math.max(grabX1, grabX2), y: Math.max(grabY1, grabY2) }, // 오른쪽아래
      { x: Math.min(grabX1, grabX2), y: Math.max(grabY1, grabY2) }, // 왼쪽아래
    ]

    for (const entity of entities) {
      // 브러시 도형은 무시
      if (entity.entityType === SHAPES.BRUSH) continue
      // listening이 false인 entity는 무시
      if (!entity.node.listening()) continue

      const entityId = entity.getId()
      // 비활성화된 entity는 무시
      if ((this as any).disabledEntityIds?.has(entityId)) continue
      if ((this as any).hiddenEntityIds?.has(entityId)) continue

      // 벌룬 검사
      if (entity.balloon) {
        const balloonRect = entity.balloon.head.rect
        const entityX = entity.node.x()
        const entityY = entity.node.y()
        const balloonCenter = {
          x: entityX + balloonRect.x(),
          y: entityY + balloonRect.y(),
        }
        const balloonStrokeWidth = balloonRect.strokeWidth() || 0
        const balloonWidth = balloonRect.width() + balloonStrokeWidth
        const balloonHeight = balloonRect.height() + balloonStrokeWidth
        const balloonTopLeft = {
          x: balloonCenter.x - balloonWidth / 2,
          y: balloonCenter.y - balloonHeight / 2,
        }
        const balloonHitBox = this.getHitBoxCorners(balloonTopLeft, balloonWidth, balloonHeight)
        if (this.checkRectHit(grabHitBox, balloonHitBox)) {
          hitEntityIds.add(entityId)
          continue
        }
      }
      // 연결선 검사
      if (entity.balloon) {
        const connectedLine = entity.balloon.connectedLine.line
        const points = connectedLine.points()
        if (points && points.length >= 4) {
          const entityX = entity.node.x()
          const entityY = entity.node.y()
          // 연결선 절대좌표
          const lineSegment: Vector2d[] = [
            { x: entityX + points[0], y: entityY + points[1] },
            { x: entityX + points[2], y: entityY + points[3] },
          ]
          // 연결선 굵기
          const lineWidth = connectedLine.strokeWidth() || 1
          const lineHitBox = this.getHitBoxCornersForLine(lineSegment, lineWidth)
          if (this.checkRectHit(grabHitBox, lineHitBox)) {
            hitEntityIds.add(entityId)
            continue
          }
        }
      }
      // 점도형
      if (entity.isPoint()) {
        const pointShape = (entity as any).__shape
        if (pointShape) {
          const shapeOffsetY = pointShape.offsetY() || 0
          const pointCenter = {
            x: entity.node.x(),
            y: entity.node.y() - shapeOffsetY,
          }
          const pointRadius = pointShape.radiusX()
          const pointStrokeWidth = pointShape.strokeWidth() || 0
          const pointSize = pointRadius * 2 + pointStrokeWidth
          const pointTopLeft = {
            x: pointCenter.x - pointSize / 2,
            y: pointCenter.y - pointSize / 2,
          }
          const pointHitBox = this.getHitBoxCorners(pointTopLeft, pointSize, pointSize)
          if (this.checkRectHit(grabHitBox, pointHitBox)) {
            hitEntityIds.add(entityId)
            continue
          }
        }
      }
      // 사각형
      if (entity.isRect()) {
        const rectShape = (entity as any).__shape
        if (rectShape) {
          const rectWidth = rectShape.width()
          const rectHeight = rectShape.height()
          const rectRotation = rectShape.rotation() || 0
          const rectTopLeft = { x: entity.node.x(), y: entity.node.y() }
          const rectHitBox = this.getHitBoxCorners(rectTopLeft, rectWidth, rectHeight, rectRotation)

          if (this.checkRectHit(grabHitBox, rectHitBox)) {
            hitEntityIds.add(entityId)
            continue
          }
        }
      }
      // 방향도형
      if (entity.isDirection()) {
        const dirShape = (entity as any).__directionShape
        const points = (entity as any).__directionPoints

        if (dirShape && points && points.length >= 4) {
          // dirShape에서 Line을 찾아 strokeWidth 가져오기
          const lines = (dirShape as IGroup).find<ILine>('Line')
          const lineWidth = lines.length > 0 ? lines[0].strokeWidth() || 1 : 1

          // 모든 선분들
          const vectorPoints: Vector2d[] = []
          for (let i = 0; i < points.length; i += 2) {
            const x = points[i]
            const y = points[i + 1]
            if (x !== undefined && y !== undefined) vectorPoints.push({ x, y })
          }

          // 모든 선분들에 대해 충돌검사
          for (let i = 0; i < vectorPoints.length - 1; i++) {
            const lineSegment = [vectorPoints[i], vectorPoints[i + 1]]
            const lineHitBox = this.getHitBoxCornersForLine(lineSegment, lineWidth)
            if (this.checkRectHit(grabHitBox, lineHitBox)) {
              hitEntityIds.add(entityId)
              break // 하나라도 충돌하면 해당 entity 추가 후 종료
            }
          }
        }
      }
      // 삼발이는 사각형으로 처리
      if (entity.isTripod()) {
        const clientRect = entity.node.getClientRect()
        const stage = this.renderer.stage
        const stagePos = stage.position()
        const stageScale = stage.scale()
        const worldX = (clientRect.x - stagePos.x) / stageScale.x
        const worldY = (clientRect.y - stagePos.y) / stageScale.y
        const worldWidth = clientRect.width / stageScale.x
        const worldHeight = clientRect.height / stageScale.y
        const entityHitBox = this.getHitBoxCorners(
          { x: worldX, y: worldY },
          worldWidth,
          worldHeight,
        )
        if (this.checkRectHit(grabHitBox, entityHitBox)) {
          hitEntityIds.add(entityId)
          continue
        }
      }
    }

    return Array.from(hitEntityIds)
  }

  // 하이라이트 갱신
  public syncHighlights(targetIds: string[] = this.grabbedShapeIds, isPreview = false): void {
    // 기존 하이라이트 모두 제거
    this.clearHighlights(isPreview)

    // 대상 도형들의 하이라이트 생성
    const entityMap = new Map(this.getEntities().map(e => [e.getId(), e]))
    const entities = targetIds
      .map(id => entityMap.get(id))
      .filter((e): e is CanvasEngineEntity => !!e)

    const targetMap = isPreview ? this.previewHighlights : this.highlights
    const systemLayer = this.renderer.layers.system
    if (!systemLayer) return

    entities.forEach(entity => {
      const entityId = entity.getId()
      const highlightShapes: { rect: IRect; rectBorder: IRect }[] = []

      // 벌룬 하이라이트
      if (entity.balloon) {
        const balloonRect = entity.balloon.head.rect
        try {
          const entityX = entity.node.x()
          const entityY = entity.node.y()
          const balloonX = balloonRect.x()
          const balloonY = balloonRect.y()

          // 벌룬 중심점 계산
          const balloonCenter = {
            x: entityX + balloonX,
            y: entityY + balloonY,
          }

          // 벌룬 크기 계산
          const balloonStrokeWidth = balloonRect.strokeWidth() || 0
          const balloonWidth = balloonRect.width() + balloonStrokeWidth
          const balloonHeight = balloonRect.height() + balloonStrokeWidth

          // 하이라이트 생성
          const rect = this.renderer.createRect({
            x: balloonCenter.x - balloonWidth / 2,
            y: balloonCenter.y - balloonHeight / 2,
            width: balloonWidth,
            height: balloonHeight,
            fill: GRAB_HIGHLIGHT_COLOR,
            opacity: GRAB_HIGHLIGHT_FILL_OPACITY,
            listening: false,
            perfectDraw: false,
          })

          const rectBorder = this.renderer.createRect({
            x: balloonCenter.x - (balloonWidth + GRAB_HIGHLIGHT_BORDER_WIDTH) / 2,
            y: balloonCenter.y - (balloonHeight + GRAB_HIGHLIGHT_BORDER_WIDTH) / 2,
            width: balloonWidth + GRAB_HIGHLIGHT_BORDER_WIDTH,
            height: balloonHeight + GRAB_HIGHLIGHT_BORDER_WIDTH,
            stroke: GRAB_HIGHLIGHT_COLOR,
            strokeWidth: GRAB_HIGHLIGHT_BORDER_WIDTH,
            listening: false,
            perfectDraw: false,
          })

          systemLayer.add(rect)
          systemLayer.add(rectBorder)
          highlightShapes.push({ rect, rectBorder })
        } catch (error) {
          console.error(error)
          // 에러 무시
        }
      }

      // 점도형 하이라이트
      if (entity.isPoint()) {
        try {
          const pointShape = (entity as any).__shape
          if (!pointShape) return

          // 점 크기 계산
          const pointRadius = pointShape.radiusX()
          const pointStrokeWidth = pointShape.strokeWidth() || 0
          const finalSize = pointRadius * 2 + pointStrokeWidth

          // 점의 실제 중심점 계산
          // pointShape에 설정된 실제 offsetY 값 사용
          const shapeOffsetY = pointShape.offsetY() || 0
          const pointCenter = {
            x: entity.node.x(),
            y: entity.node.y() - shapeOffsetY,
          }

          const rect = this.renderer.createRect({
            x: pointCenter.x - finalSize / 2,
            y: pointCenter.y - finalSize / 2,
            width: finalSize,
            height: finalSize,
            fill: GRAB_HIGHLIGHT_COLOR,
            opacity: GRAB_HIGHLIGHT_FILL_OPACITY,
            listening: false,
            perfectDraw: false,
          })

          const rectBorder = this.renderer.createRect({
            x: pointCenter.x - (finalSize + GRAB_HIGHLIGHT_BORDER_WIDTH) / 2,
            y: pointCenter.y - (finalSize + GRAB_HIGHLIGHT_BORDER_WIDTH) / 2,
            width: finalSize + GRAB_HIGHLIGHT_BORDER_WIDTH,
            height: finalSize + GRAB_HIGHLIGHT_BORDER_WIDTH,
            stroke: GRAB_HIGHLIGHT_COLOR,
            strokeWidth: GRAB_HIGHLIGHT_BORDER_WIDTH,
            listening: false,
            perfectDraw: false,
          })

          systemLayer.add(rect)
          systemLayer.add(rectBorder)
          highlightShapes.push({ rect, rectBorder })
        } catch (error) {
          console.error(error)
          // 에러 무시
        }
      }
      if (highlightShapes.length > 0) targetMap.set(entityId, highlightShapes)
    })

    // 일반 하이라이트인 경우 그룹 경계 박스도 업데이트
    if (!isPreview) this.syncGrabbedBox()
  }

  // 하이라이트 삭제
  private clearHighlights(isPreview = false): void {
    const targetMap = isPreview ? this.previewHighlights : this.highlights
    const layersToRedraw = new Set<ILayer>()

    targetMap.forEach(shapes => {
      shapes.forEach(({ rect, rectBorder }) => {
        const layer = rect.getLayer()
        if (layer) layersToRedraw.add(layer)
        rect.destroy()
        rectBorder.destroy()
      })
    })

    targetMap.clear()
    layersToRedraw.forEach(layer => layer.batchDraw())
  }

  // 그랩 박스 드래그 시작
  private handleGrabBoxDragStart(): void {
    const pos = this.getPointerPosition()

    // 정리
    this.clearGrabBox() // 기존 그랩박스
    this.clearHighlights(true) // 미리보기 하이라이트

    // ctrl/cmd 키 상태
    this.grabBox.isCtrlPressed = this.isCtrlPressed
    // ctrl/cmd 안누른 상태
    if (!this.grabBox.isCtrlPressed) {
      // 그룹 경계 박스 제거
      this.clearGrabbedBox()
      if (this.isGrabMode) {
        // 하이라이트 제거하고 새로 시작
        this.clearHighlights(false)
        this.clearActionButton() // 액션버튼 그룹 정리
        const beforeGrabbedShapeIds = cloneDeep(this.grabbedShapeIds)
        this.grabbedShapeIds = []
        this.isGrabMode = false
        if (beforeGrabbedShapeIds.length > 0) {
          const entities = beforeGrabbedShapeIds.map(id => this.getEntityById(id))
          if (entities.every(entity => entity !== undefined) && entities.length > 0) {
            this.restoreTargetCache(entities)
          }
        }
      }
    }

    // 그랩 영역 그리기 시작
    this.grabBox.isDrawing = true
    this.grabBox.startX = pos.x
    this.grabBox.startY = pos.y

    // grab 상태에 맞는 도형 리스닝 설정
    this.setEntityListeningForGrab()
  }

  // 그랩 박스 드래그
  private handleGrabBoxDrag(): void {
    if (!this.grabBox.isDrawing) return
    const pos = this.getPointerPosition()

    // 기존 애니메이션 프레임 취소
    if (this.grabBox.animationId) cancelAnimationFrame(this.grabBox.animationId)

    // 그랩 박스 업데이트
    this.grabBox.animationId = requestAnimationFrame(() => {
      const [x1, y1, x2, y2] = [this.grabBox.startX, this.grabBox.startY, pos.x, pos.y]

      const width = Math.abs(x2 - x1)
      const height = Math.abs(y2 - y1)

      // 최소 크기 체크
      const MIN_SIZE = 3
      if (width < MIN_SIZE || height < MIN_SIZE) return

      const systemLayer = this.renderer.layers.system
      if (!systemLayer) return

      // 배경 사각형
      if (!this.grabBox.rect) {
        this.grabBox.rect = this.renderer.createRect({
          listening: false,
          perfectDraw: false,
        })
        systemLayer.add(this.grabBox.rect)
      }

      // 테두리 사각형
      if (!this.grabBox.rectBorder) {
        this.grabBox.rectBorder = this.renderer.createRect({
          listening: false,
          perfectDraw: false,
        })
        systemLayer.add(this.grabBox.rectBorder)
      }

      // 배경 사각형 업데이트
      this.grabBox.rect
        .x(Math.min(x1, x2))
        .y(Math.min(y1, y2))
        .width(width)
        .height(height)
        .fill(GRAB_BOX_COLOR)
        .opacity(GRAB_BOX_FILL_OPACITY)

      // 테두리 사각형 업데이트
      this.grabBox.rectBorder
        .x(Math.min(x1, x2))
        .y(Math.min(y1, y2))
        .width(width)
        .height(height)
        .stroke(GRAB_BOX_COLOR)
        .strokeWidth(GRAB_BOX_BORDER_WIDTH)
        .fill(undefined)

      systemLayer.batchDraw()
      this.grabBox.animationId = undefined
    })

    // 미리보기 하이라이트 업데이트
    const currentGrabbedIds = this.findOnGrabBoxEntities(
      this.grabBox.startX,
      this.grabBox.startY,
      pos.x,
      pos.y,
    )
    const previewTargetIds = currentGrabbedIds.filter(
      entityId => !this.grabbedShapeIds.includes(entityId),
    )
    this.syncHighlights(previewTargetIds, true)
  }

  // 그랩 박스 드래그 종료
  private handleGrabEnd(): void {
    if (!this.grabBox.isDrawing) return

    const pos = this.getPointerPosition()

    // 그랩박스 크기 계산
    const grabWidth = Math.abs(pos.x - this.grabBox.startX)
    const grabHeight = Math.abs(pos.y - this.grabBox.startY)
    const isEmptyClick = grabWidth < GRAB_BOX_MIN_DISTANCE && grabHeight < GRAB_BOX_MIN_DISTANCE

    // ctrl/cmd 상태 저장
    const wasCtrlPressed = this.grabBox.isCtrlPressed

    // 미리보기 하이라이트 제거
    this.clearHighlights(true)

    // 그랩 영역 정리
    this.clearGrabBox()

    // grab 상태에 따라 도형 이벤트 리스닝 설정
    this.setEntityListeningForGrab()

    // 빈 공간 클릭인 경우
    if (isEmptyClick && !this.isDraggingGrabBox) {
      // ctrl/cmd가 눌리지 않았으면 그랩 모드 해제
      if (!wasCtrlPressed) this.exitGrabMode()
      return
    }

    // 그랩 박스 범위 내에 있는 entity들 찾기
    const newGrabbedIds = this.findOnGrabBoxEntities(
      this.grabBox.startX,
      this.grabBox.startY,
      pos.x,
      pos.y,
    )

    // ctrl/cmd 상태에 따른 그랩 처리
    let finalGrabbedIds: string[]
    if (wasCtrlPressed) {
      // ctrl/cmd가 눌려있었으면 추가
      finalGrabbedIds = Array.from(new Set([...this.grabbedShapeIds, ...newGrabbedIds]))
    } else {
      // ctrl/cmd가 안 눌려있었으면 대체
      finalGrabbedIds = newGrabbedIds
    }

    // 그랩 모드 진입/해제 처리
    if (finalGrabbedIds.length > 0) {
      this.enterGrabModeWithIds(finalGrabbedIds)

      // 그랩 모드가 유지되는 경우에만 뷰포트 조정
      if (this.isGrabMode) {
        const shouldFocusViewport = !wasCtrlPressed || newGrabbedIds.length > 0
        if (shouldFocusViewport) this.focusGrabbedBox()
      }
    } else {
      // 그랩된 엔티티가 없으면 그랩 모드 해제
      this.exitGrabMode()
    }
  }

  // 지정된 ID 목록으로 grab 모드 진입 (공통 로직)
  private enterGrabModeWithIds(ids: string[]): void {
    this.isGrabMode = true
    this.grabbedShapeIds = ids
    const entities = ids.map(id => this.getEntityById(id))
    if (entities.every(e => e !== undefined) && entities.length > 0) {
      this.removeTargetCache(entities)
    }
    this.setEntityListeningForGrab()
    this.checkAndHandleSingleGrab()
    if (this.isGrabMode) {
      this.events.onGrabShapes(this.grabbedShapeIds, this.calculateGrabbedBox(this.renderer.stage))
      this.syncGrabbedBox()
    }
  }

  // 뷰포트 조정
  private focusGrabbedBox(): void {
    if (this.grabbedShapeIds.length === 0) return
    // 1개 도형인 경우 뷰포트 조정 비활성화
    if (this.grabbedShapeIds.length === 1) return

    const stage = this.renderer.stage
    const grabbedBox = this.calculateGrabbedBox(stage)
    if (!grabbedBox) return

    // 스테이지 정보
    const stageWidth = stage.width()
    const stageHeight = stage.height()
    const currentScale = stage.scaleX()

    // 그랩된 엔티티들의 중심점 계산
    const centerX = grabbedBox.x + grabbedBox.width / 2
    const centerY = grabbedBox.y + grabbedBox.height / 2

    // 여백을 고려한 목표 영역 계산
    const targetViewportWidth = (stageWidth * 0.8) / currentScale
    const targetViewportHeight = (stageHeight * 0.8) / currentScale

    // 그랩된 영역이 화면에 맞도록 스케일 계산
    let targetScale = currentScale
    if (grabbedBox.width > targetViewportWidth || grabbedBox.height > targetViewportHeight) {
      const scaleX = targetViewportWidth / grabbedBox.width
      const scaleY = targetViewportHeight / grabbedBox.height
      targetScale = Math.min(scaleX, scaleY, this.getMaxZoom())
      targetScale = Math.max(targetScale, this.getMinZoom())
    }

    // 화면 중앙 좌표
    const centerScreenX = stageWidth / 2 / targetScale
    const centerScreenY = stageHeight / 2 / targetScale

    // 최종 타겟 좌표 계산
    const targetX = (centerScreenX - centerX) * targetScale
    const targetY = (centerScreenY - centerY) * targetScale

    // 이동
    const tween = this.renderer.tween(
      stage,
      { x: targetX, y: targetY, scaleX: targetScale, scaleY: targetScale },
      {
        duration: 0.4,
        easing: 'easeOut',
        onFinish: (): void => {
          // 뷰포트 변경 이벤트 발생 전파
          this.events.onViewportChanged({
            x: stage.x(),
            y: stage.y(),
            zoom: stage.scaleX(),
          })
        },
      },
    )

    tween.play()
  }

  // 그랩 박스 정리
  private clearGrabBox(): void {
    if (this.grabBox.animationId) {
      cancelAnimationFrame(this.grabBox.animationId)
      this.grabBox.animationId = undefined
    }
    if (this.grabBox.rect) {
      this.grabBox.rect.destroy()
      this.grabBox.rect = undefined
    }
    if (this.grabBox.rectBorder) {
      this.grabBox.rectBorder.destroy()
      this.grabBox.rectBorder = undefined
    }
    this.grabBox.isDrawing = false
    this.grabBox.isCtrlPressed = false
  }

  // 그랩된 모든 도형들의 경계 사각형 계산
  private calculateGrabbedBox(
    stage: IStage,
  ): { x: number; y: number; width: number; height: number } | null {
    if (this.grabbedShapeIds.length === 0) return null

    const stagePos = stage.position()
    const stageScale = stage.scale()

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    // 그랩된 모든 entity들의 경계를 계산
    this.grabbedShapeIds.forEach(entityId => {
      const entity = this.getEntities().find(e => e.getId() === entityId)
      if (!entity) return
      const clientRect = entity.node.getClientRect()

      // 전역 좌표로
      const worldX = (clientRect.x - stagePos.x) / stageScale.x
      const worldY = (clientRect.y - stagePos.y) / stageScale.y
      const worldWidth = clientRect.width / stageScale.x
      const worldHeight = clientRect.height / stageScale.y

      const entityRect = {
        x: worldX,
        y: worldY,
        width: worldWidth,
        height: worldHeight,
      }
      if (!entityRect) return

      minX = Math.min(minX, entityRect.x)
      minY = Math.min(minY, entityRect.y)
      maxX = Math.max(maxX, entityRect.x + entityRect.width)
      maxY = Math.max(maxY, entityRect.y + entityRect.height)
    })

    // 유효한 경계가 계산되었는지 확인
    if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
      return null
    }

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    }
  }

  // 그룹 경계 박스 생성
  private createGrabbedBox(boundingRect: {
    x: number
    y: number
    width: number
    height: number
  }): void {
    const systemLayer = this.renderer.layers.system
    if (!systemLayer) return

    // 기존 그룹 경계 박스 제거
    this.clearGrabbedBox()

    // 배경 사각형 생성
    const rect = this.renderer.createRect({
      x: boundingRect.x,
      y: boundingRect.y,
      width: boundingRect.width,
      height: boundingRect.height,
      fill: GRAB_BOX_COLOR,
      opacity: GRAB_BOX_FILL_OPACITY,
      listening: false,
      perfectDraw: false,
    })

    // 테두리 사각형 생성
    const rectBorder = this.renderer.createRect({
      x: boundingRect.x - GRAB_BOX_BORDER_WIDTH / 2,
      y: boundingRect.y - GRAB_BOX_BORDER_WIDTH / 2,
      width: boundingRect.width + GRAB_BOX_BORDER_WIDTH,
      height: boundingRect.height + GRAB_BOX_BORDER_WIDTH,
      stroke: GRAB_BOX_COLOR,
      strokeWidth: GRAB_BOX_BORDER_WIDTH,
      fill: undefined,
      listening: false,
      perfectDraw: false,
    })

    systemLayer.add(rect)
    systemLayer.add(rectBorder)
    this.grabbedBox.rect = rect
    this.grabbedBox.rectBorder = rectBorder
    systemLayer.batchDraw()
  }

  // 그룹 경계 박스 갱신
  private syncGrabbedBox(): void {
    const stage = this.renderer.stage
    if (!stage) return

    const shouldShow =
      this.isGrabMode && // 그랩 모드이면서
      (!this.grabBox.isDrawing || this.grabBox.isCtrlPressed) && // 그랩박스를 그리는 중이 아니거나, ctrl+그랩박스인 경우
      this.grabbedShapeIds.length > 0 // 그랩된 도형이 있을 때

    if (shouldShow) {
      const boundingRect = this.calculateGrabbedBox(stage)
      if (boundingRect) {
        if (this.grabbedBox.rect && this.grabbedBox.rectBorder) {
          // 기존 박스 업데이트
          this.grabbedBox.rect
            .x(boundingRect.x)
            .y(boundingRect.y)
            .width(boundingRect.width)
            .height(boundingRect.height)
          this.grabbedBox.rectBorder
            .x(boundingRect.x - GRAB_BOX_BORDER_WIDTH / 2)
            .y(boundingRect.y - GRAB_BOX_BORDER_WIDTH / 2)
            .width(boundingRect.width + GRAB_BOX_BORDER_WIDTH)
            .height(boundingRect.height + GRAB_BOX_BORDER_WIDTH)
          this.grabbedBox.rect.getLayer()?.batchDraw()
        } else {
          // 새 박스 생성
          this.createGrabbedBox(boundingRect)
        }
      } else {
        // 경계 계산 실패 시 제거
        this.clearGrabbedBox()
      }
    } else {
      // 조건에 맞지 않으면 제거
      this.clearGrabbedBox()
    }
  }

  // 그룹 경계 박스 제거
  private clearGrabbedBox(): void {
    if (this.grabbedBox.rect) {
      const layer = this.grabbedBox.rect.getLayer()
      this.grabbedBox.rect.destroy()
      this.grabbedBox.rect = null

      if (layer) {
        layer.batchDraw()
      }
    }
    if (this.grabbedBox.rectBorder) {
      this.grabbedBox.rectBorder.destroy()
      this.grabbedBox.rectBorder = null
    }
  }

  // ctrl+클릭으로 다중 선택 토글 (grab 모드) 또는 단일→다중 전환
  public ctrlSelectEntity(clickedEntity: CanvasEngineEntity): void {
    const clickedId = clickedEntity.getId()

    if (this.isGrabMode) {
      // 이미 grab 모드: 토글 (추가/제거)
      const isCurrentlyGrabbed = this.grabbedShapeIds.includes(clickedId)
      if (!isCurrentlyGrabbed) {
        this.grabbedShapeIds.push(clickedId)
      } else {
        this.grabbedShapeIds = this.grabbedShapeIds.filter(id => id !== clickedId)
      }
      this.checkAndHandleSingleGrab()
      this.events.onGrabShapes(this.grabbedShapeIds, this.calculateGrabbedBox(this.renderer.stage))
      return
    }

    // 단일 선택 → 다중 선택 전환
    if (!this.selectedEntity) {
      // 선택된 도형이 없으면 일반 단일 선택
      this.selectShape(clickedEntity)
      return
    }
    const currentId = this.selectedEntity.getId()
    if (currentId === clickedId) return

    // selection UI 정리 (onLostFocus 미호출 — URL sync → exitGrabMode 방지)
    this.selectedEntity.blur()
    this.moveEntityToLayer(this.selectedEntity, this.selectedEntity.getLayerId())
    this.selectedEntity = undefined
    if (this.__lineTransformerEndHandle) this.__lineTransformerEndHandle.destroy()
    if (this.__lineTransformerStartHandle) this.__lineTransformerStartHandle.destroy()
    if (this.__shapeTransformer) this.__shapeTransformer.destroy()

    this.enterGrabModeWithIds([currentId, clickedId])
  }

  // 그랩된 도형 수에 따른 처리
  public checkAndHandleSingleGrab(): void {
    if (this.grabbedShapeIds.length === 0) {
      // 그랩된 도형이 없으면 그랩 모드 해제
      this.exitGrabMode()
      return
    } else if (this.grabbedShapeIds.length === 1) {
      // 그랩된 도형이 1개이면 그랩 모드 종료하고 해당 도형 선택
      const singleEntityId = this.grabbedShapeIds[0]
      const entity = this.getEntities().find(e => e.getId() === singleEntityId)

      if (entity) {
        // 그랩 모드 종료
        this.exitGrabMode()
        // 해당 도형 선택
        super.selectShape(entity, singleEntityId)
      } else {
        // 엔티티를 찾을 수 없으면 그랩 모드만 종료
        this.exitGrabMode()
      }
      return
    } else {
      // 2개 이상이면 그랩 모드 유지
      // 하이라이트 동기화
      this.syncHighlights(this.grabbedShapeIds)

      // ActionButtonGroup 표시
      this.showGrabBoxActionButton()
    }
  }

  // 좌표가 사각형 안에 있는지 확인하는 함수
  private isPointInRect(
    x: number,
    y: number,
    rect: { x: number; y: number; width: number; height: number },
  ): boolean {
    return x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height
  }

  // 클릭 위치에서 엔티티 찾기
  private findEntityFromClick(e: IPointerEvent): CanvasEngineEntity | null {
    const stage = this.renderer.stage

    // 좌표 변환
    const stageRect = stage.container().getBoundingClientRect()
    const evt = e.evt as MouseEvent
    const browserX = evt.clientX - stageRect.left
    const browserY = evt.clientY - stageRect.top
    const transform = stage.getAbsoluteTransform().copy().invert()
    const stageCoords = transform.point({ x: browserX, y: browserY })

    // 모든 엔티티 검사
    for (const entity of this.getEntities()) {
      // 비활성화된 entity는 무시
      if ((this as any).disabledEntityIds?.has(entity.getId())) continue
      if ((this as any).hiddenEntityIds?.has(entity.getId())) continue

      // 벌룬 영역 확인
      if (entity.balloon) {
        try {
          const balloonRect = entity.balloon.head.rect.getClientRect({
            relativeTo: stage,
          })
          if (this.isPointInRect(stageCoords.x, stageCoords.y, balloonRect)) return entity
        } catch {
          // 에러 무시
        }
      }

      // 본도형 영역 확인
      const shape = (entity as any).__shape
      if (shape) {
        try {
          const shapeRect = (shape as INode).getClientRect({ relativeTo: stage })
          if (this.isPointInRect(stageCoords.x, stageCoords.y, shapeRect)) return entity
        } catch {
          // 에러 무시
        }
      }
    }

    return null
  }

  // 도형 이벤트리스닝 관리
  private setEntityListeningForGrab(): void {
    // 그리기 중이거나 그랩 모드인 경우 비활성화
    const enable: boolean = !(this.grabBox.isDrawing || this.isGrabMode)
    const entities = this.getEntities()
    entities.forEach(entity => {
      entity.node.listening(enable)
    })
  }

  // selectShape 오버라이드
  override selectShape(entity: CanvasEngineEntity, originalShapeId?: string): void {
    // 그랩 모드인 경우 선택 방지
    if (this.isGrabMode) return
    // 마지막 도형 해제 후 선택 방지
    if (this.preventNextGrab) return
    super.selectShape(entity, originalShapeId)
  }

  // ActionButtonGroup 표시
  private async showGrabBoxActionButton(): Promise<void> {
    if (this.grabbedShapeIds.length === 0) return

    // 기존 ActionButtonGroup 제거
    this.clearActionButton()

    // 첫 번째 entity 기준으로 brush 여부 확인
    const firstEntity = this.getEntities().find(e => e.getId() === this.grabbedShapeIds[0])
    if (!firstEntity) return

    // 버튼그룹을 위한 컨테이너 생성
    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.zIndex = '1000'
    container.style.pointerEvents = 'auto'
    container.setAttribute('data-grab-action-button', 'true')

    // body에 추가
    document.body.appendChild(container)
    this.grabBoxButtonContainer = container

    try {
      const { createApp } = await import('vue')
      const ActionButtonGroup = (await import('@/components/Editor/ActionButtonGroup.vue')).default

      // Vue 앱 생성
      this.grabBoxButtonApp = createApp(ActionButtonGroup, {
        onDelete: (shapeIds: string[]) => this.events.onDeleteShapes(shapeIds),
        onCombine: (combineShapeIds: string[]) => this.events.onCombineShape?.(combineShapeIds),
      })

      // 앱 마운트
      this.grabBoxButtonApp.mount(container)

      // ActionButtonGroup 요소 참조 설정
      requestAnimationFrame(() => {
        const buttonElement = container.firstElementChild as HTMLElement
        if (buttonElement) this.setGrabBoxButtonElement(buttonElement)
      })
    } catch (error) {
      console.error(error)
      // 실패 시 컨테이너 정리
      this.clearActionButton()
    }
  }

  // ActionButtonGroup 제거
  private clearActionButton(): void {
    try {
      // 실시간 동기화 정지
      this.stopGrabBoxButtonPositionSync()

      // ResizeObserver 정리
      this.grabBoxButtonResizeObserver?.disconnect()
      this.grabBoxButtonResizeObserver = undefined

      // Vue 앱 unmount
      if (this.grabBoxButtonApp) {
        try {
          this.grabBoxButtonApp.unmount()
        } catch (error) {
          console.warn('ActionButton 앱 unmount 중 오류:', error)
        }
        this.grabBoxButtonApp = null
      }

      // 컨테이너 제거
      if (this.grabBoxButtonContainer) {
        try {
          if (this.grabBoxButtonContainer.parentNode) {
            this.grabBoxButtonContainer.parentNode.removeChild(this.grabBoxButtonContainer)
          }
          this.grabBoxButtonContainer.remove()
        } catch (error) {
          console.warn('ActionButton 컨테이너 제거 중 오류:', error)
        }
        this.grabBoxButtonContainer = null
      }

      // 경계 정보 및 캐시 초기화
      this.lastGrabbedBoxBounds = null
      this.lastStagePosition = { x: 0, y: 0 }
      this.lastStageScale = 1
      this.grabBoxButtonElement = null

      // 남은 ActionButtonGroup 요소들 강제 제거
      try {
        const existingButtons = document.querySelectorAll('[data-grab-action-button]')
        existingButtons.forEach(button => {
          if (button.parentNode) {
            button.parentNode.removeChild(button)
          }
        })
      } catch (error) {
        console.warn('남은 ActionButton 요소 정리 중 오류:', error)
      }
    } catch (error) {
      console.error('ActionButton 정리 중 전체 오류:', error)
    }
  }

  // ActionButtonGroup 요소 설정
  public setGrabBoxButtonElement(element: HTMLElement): void {
    this.grabBoxButtonElement = element

    // 초기 위치 캐시 설정
    this.initializePositionCache()

    // 초기 위치 설정
    this.updateGrabBoxActionButtonPosition()

    // 컴포넌트 크기 변화 시 위치 재계산 (최소화/펼치기 등)
    if (typeof ResizeObserver !== 'undefined') {
      this.grabBoxButtonResizeObserver?.disconnect()
      this.grabBoxButtonResizeObserver = new ResizeObserver(() => {
        if (this.grabBoxButtonElement && this.isGrabMode) {
          this.updateGrabBoxActionButtonPosition()
        }
      })
      this.grabBoxButtonResizeObserver.observe(element)
    }

    // 실시간 위치 동기화 시작
    this.startGrabBoxButtonPositionSync()
  }

  // 위치 캐시 초기화
  private initializePositionCache(): void {
    const stage = this.renderer.stage
    this.lastStageScale = stage.scaleX()
    this.lastStagePosition = { ...stage.position() }

    // grabbedBox 경계도 캐시
    const grabbedBox = this.calculateGrabbedBox(stage)
    if (grabbedBox) {
      this.lastGrabbedBoxBounds = { ...grabbedBox }
    }
  }

  // ActionButtonGroup 위치 업데이트
  private updateGrabBoxActionButtonPosition(): void {
    if (!this.grabBoxButtonElement || this.grabbedShapeIds.length === 0) return

    const stage = this.renderer.stage
    if (!stage) return

    const grabbedBox = this.calculateGrabbedBox(stage)
    if (!grabbedBox) return

    const scale = stage.scaleX()
    const stagePos = stage.position()
    const containerRect = stage.container().getBoundingClientRect()

    // canvas 좌표 계산 (grabbedBox 하단 중앙)
    const canvasButtonCenterX = grabbedBox.x + grabbedBox.width / 2
    const canvasButtonCenterY =
      grabbedBox.y + grabbedBox.height + GRAB_BUTTON_POSITION_CONFIG.GRABBOX_OFFSET_BELOW / scale

    // canvas 좌표를 DOM 좌표로 변환
    const domCenterX = containerRect.left + (canvasButtonCenterX * scale + stagePos.x)
    const domCenterY = containerRect.top + (canvasButtonCenterY * scale + stagePos.y)

    // 실제 엘리먼트 크기 가져오기
    const elementWidth =
      this.grabBoxButtonElement.offsetWidth || GRAB_BUTTON_POSITION_CONFIG.DEFAULT_ELEMENT_WIDTH
    const elementHeight =
      this.grabBoxButtonElement.offsetHeight || GRAB_BUTTON_POSITION_CONFIG.DEFAULT_ELEMENT_HEIGHT

    // 중심점을 기준으로 왼쪽 상단 좌표 계산
    const finalX = domCenterX - elementWidth / 2
    const finalY = domCenterY - elementHeight / 2

    // 화면 경계 체크 및 조정
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    let adjustedX = finalX
    let adjustedY = finalY

    // 화면 경계를 벗어나지 않도록 조정
    if (adjustedX < GRAB_BUTTON_POSITION_CONFIG.SCREEN_MARGIN) {
      adjustedX = GRAB_BUTTON_POSITION_CONFIG.SCREEN_MARGIN
    }
    if (adjustedY < GRAB_BUTTON_POSITION_CONFIG.SCREEN_MARGIN) {
      adjustedY = GRAB_BUTTON_POSITION_CONFIG.SCREEN_MARGIN
    }
    if (adjustedX + elementWidth > windowWidth - GRAB_BUTTON_POSITION_CONFIG.SCREEN_MARGIN) {
      adjustedX = windowWidth - elementWidth - GRAB_BUTTON_POSITION_CONFIG.SCREEN_MARGIN
    }
    if (adjustedY + elementHeight > windowHeight - GRAB_BUTTON_POSITION_CONFIG.SCREEN_MARGIN) {
      adjustedY = windowHeight - elementHeight - GRAB_BUTTON_POSITION_CONFIG.SCREEN_MARGIN
    }

    // 요소 위치 설정
    this.grabBoxButtonElement.style.position = 'fixed'
    this.grabBoxButtonElement.style.left = `${adjustedX}px`
    this.grabBoxButtonElement.style.top = `${adjustedY}px`
    this.grabBoxButtonElement.style.transform = 'none'
    this.grabBoxButtonElement.style.transformOrigin = 'top left'
    this.grabBoxButtonElement.style.zIndex = '1000'
    this.grabBoxButtonElement.style.pointerEvents = 'auto'
  }

  // 실시간 위치 동기화 시작
  private startGrabBoxButtonPositionSync(): void {
    this.stopGrabBoxButtonPositionSync() // 기존 동기화 정리

    const syncPosition = (): void => {
      if (this.grabBoxButtonElement && this.isGrabMode && this.grabbedShapeIds.length > 0) {
        // 변경 감지: stage position, scale, grabbedBox가 변경되었을 때만 업데이트
        if (this.hasPositionChanged()) this.updateGrabBoxActionButtonPosition()
        this.grabBoxButtonAnimationFrameId = requestAnimationFrame(syncPosition)
      }
    }
    this.grabBoxButtonAnimationFrameId = requestAnimationFrame(syncPosition)
  }

  // 실시간 위치 동기화 정지
  private stopGrabBoxButtonPositionSync(): void {
    if (this.grabBoxButtonAnimationFrameId) {
      cancelAnimationFrame(this.grabBoxButtonAnimationFrameId)
      this.grabBoxButtonAnimationFrameId = undefined
    }
  }

  // stage position이나 scale, grabbedBox가 변경되었는지 확인
  private hasPositionChanged(): boolean {
    const stage = this.renderer.stage
    const currentScale = stage.scaleX()
    const currentStagePos = stage.position()

    // grabbedBox 경계도 확인
    const currentBox = this.calculateGrabbedBox(stage)

    const scaleChanged = Math.abs(this.lastStageScale - currentScale) > 0.001
    const stagePositionChanged =
      Math.abs(this.lastStagePosition.x - currentStagePos.x) > 1 ||
      Math.abs(this.lastStagePosition.y - currentStagePos.y) > 1

    // grabbedBox 경계 변경 확인
    let grabbedBoxChanged = false
    if (currentBox && this.lastGrabbedBoxBounds) {
      const threshold = 1
      grabbedBoxChanged =
        Math.abs(this.lastGrabbedBoxBounds.x - currentBox.x) > threshold ||
        Math.abs(this.lastGrabbedBoxBounds.y - currentBox.y) > threshold ||
        Math.abs(this.lastGrabbedBoxBounds.width - currentBox.width) > threshold ||
        Math.abs(this.lastGrabbedBoxBounds.height - currentBox.height) > threshold
    } else if (currentBox !== this.lastGrabbedBoxBounds) {
      grabbedBoxChanged = true
    }

    if (scaleChanged || stagePositionChanged || grabbedBoxChanged) {
      // 변경된 값들 저장
      this.lastStageScale = currentScale
      this.lastStagePosition = { ...currentStagePos }
      if (currentBox) {
        this.lastGrabbedBoxBounds = { ...currentBox }
      }
      return true
    }

    return false
  }

  // 두 사각형이 겹치는지 확인
  // 매개변수는 길이가 4 이상이며 왼쪽위 꼭짓점부터 시계방향임 (4개 이상이면 첫 4개만 사용)
  private checkRectHit(rectA: Vector2d[], rectB: Vector2d[]): boolean {
    if (rectA.length < 4 || rectB.length < 4) {
      console.error('Wrong Parameter: rectA and rectB must have at least 4 corners each.')
      return false
    }

    // 4개 이상이면 첫 4개만 사용
    const rectACorners = rectA.slice(0, 4)
    const rectBCorners = rectB.slice(0, 4)

    // SAT를 위한 모든 축 수집
    const axes: Vector2d[] = []

    // rectA의 모든 변에 수직인 축들
    for (let i = 0; i < 4; i++) {
      const current = rectACorners[i]
      const next = rectACorners[(i + 1) % 4]
      // 변의 방향 벡터
      const edge = { x: next.x - current.x, y: next.y - current.y }
      // 수직 벡터 (법선 벡터)
      const normal = { x: -edge.y, y: edge.x }
      // 정규화
      const length = Math.sqrt(normal.x * normal.x + normal.y * normal.y)
      if (length > 0) {
        axes.push({ x: normal.x / length, y: normal.y / length })
      }
    }

    // rectB
    for (let i = 0; i < 4; i++) {
      const current = rectBCorners[i]
      const next = rectBCorners[(i + 1) % 4]
      const edge = { x: next.x - current.x, y: next.y - current.y }
      const normal = { x: -edge.y, y: edge.x }
      const length = Math.sqrt(normal.x * normal.x + normal.y * normal.y)
      if (length > 0) {
        axes.push({ x: normal.x / length, y: normal.y / length })
      }
    }

    // 각 축에 대해 충돌 검사
    for (const axis of axes) {
      // rectA
      let minA = Number.POSITIVE_INFINITY
      let maxA = Number.NEGATIVE_INFINITY
      for (const vertex of rectACorners) {
        const projection = vertex.x * axis.x + vertex.y * axis.y
        minA = Math.min(minA, projection)
        maxA = Math.max(maxA, projection)
      }

      // rectB
      let minB = Number.POSITIVE_INFINITY
      let maxB = Number.NEGATIVE_INFINITY
      for (const vertex of rectBCorners) {
        const projection = vertex.x * axis.x + vertex.y * axis.y
        minB = Math.min(minB, projection)
        maxB = Math.max(maxB, projection)
      }

      // 분리 여부 확인
      if (maxA < minB || maxB < minA) return false // 미충돌
    }
    return true // 충돌
  }

  // 왼쪽위 좌표, 너비, 높이, 회전각도로 사각형의 네 꼭짓점 계산
  private getHitBoxCorners(
    topLeft: Vector2d,
    width: number,
    height: number,
    rotation: number = 0,
  ): Vector2d[] {
    // 회전각도 변환
    const theta = (rotation * Math.PI) / 180

    const cos = Math.cos(theta)
    const sin = Math.sin(theta)

    // 회전의 중심점
    const cx = topLeft.x
    const cy = topLeft.y

    // 회전하지 않았을 때의 각 꼭짓점 좌표
    const topLeftCorner = { x: topLeft.x, y: topLeft.y }
    const topRightCorner = { x: topLeft.x + width, y: topLeft.y }
    const bottomRightCorner = { x: topLeft.x + width, y: topLeft.y + height }
    const bottomLeftCorner = { x: topLeft.x, y: topLeft.y + height }

    const points = [topLeftCorner, topRightCorner, bottomRightCorner, bottomLeftCorner]

    // 꼭짓점 회전 변환
    const rotatedVertices = points.map(point => {
      // 중심점을 기준으로 상대 좌표 계산
      const dx = point.x - cx
      const dy = point.y - cy

      // 회전 변환 적용
      const rotatedX = dx * cos - dy * sin
      const rotatedY = dx * sin + dy * cos

      // 다시 원래 좌표계로 이동
      return {
        x: cx + rotatedX,
        y: cy + rotatedY,
      }
    })

    return rotatedVertices
  }

  // 선분(2점)과 선 굵기로 둘러싸인 사각형의 네 꼭짓점 계산
  private getHitBoxCornersForLine(lineSegment: Vector2d[], lineWidth: number): Vector2d[] {
    if (lineSegment.length < 2) {
      console.error('Wrong Parameter: lineSegment must have at least 2 points.')
      return []
    }
    const OFFSET = 4 // 선 굵기 외에 추가로 확장할 폭

    // 2개 이상이면 첫 2개만 사용
    const [startPoint, endPoint] = lineSegment.slice(0, 2)

    // 선분의 방향 벡터 계산
    const direction = {
      x: endPoint.x - startPoint.x,
      y: endPoint.y - startPoint.y,
    }

    // 선분의 길이 계산
    const length = Math.sqrt(direction.x * direction.x + direction.y * direction.y)

    // 길이가 0인 점인 경우 처리
    if (length === 0) {
      const halfWidth = (lineWidth + 4) / 2
      return [
        { x: startPoint.x - halfWidth, y: startPoint.y - halfWidth },
        { x: startPoint.x + halfWidth, y: startPoint.y - halfWidth },
        { x: startPoint.x + halfWidth, y: startPoint.y + halfWidth },
        { x: startPoint.x - halfWidth, y: startPoint.y + halfWidth },
      ]
    }

    // 정규화된 방향 벡터
    const normalizedDirection = {
      x: direction.x / length,
      y: direction.y / length,
    }

    // 선분에 수직인 방향
    const perpendicular = {
      x: -normalizedDirection.y,
      y: normalizedDirection.x,
    }

    // 사각형의 폭
    const rectWidth = lineWidth + OFFSET
    const halfWidth = rectWidth / 2

    // 확장
    const offset = {
      x: perpendicular.x * halfWidth,
      y: perpendicular.y * halfWidth,
    }

    // 네 꼭짓점 계산
    return [
      {
        x: startPoint.x + offset.x,
        y: startPoint.y + offset.y,
      },
      {
        x: endPoint.x + offset.x,
        y: endPoint.y + offset.y,
      },
      {
        x: endPoint.x - offset.x,
        y: endPoint.y - offset.y,
      },
      {
        x: startPoint.x - offset.x,
        y: startPoint.y - offset.y,
      },
    ]
  }
}
